<script setup lang="ts">
import type AntiRobot from '@/components/base/form/AntiRobot.vue';
import type inputFullPhone from '@/components/base/form/InputFullPhone.vue';
import type Documents from '@/components/base/form/Documents.vue';
import type SelectCountry from '@/components/base/form/SelectCountry.vue';
import { useUserJourney } from '~/composables/apostatudo/use-user-journey';

const { $metrics } = useNuxtApp();
const { t } = useTexts();
const { currentRoute, push } = useRouter();

const { isTaboolaEnabled, isKwaiPixelEnabled } = useFeatureFlagsEnv();
const { isLudopathyValidationEnabled } = useFeatureFlagsNew();

const { closeModal, openToaster } = useLayoutStore();
const { isAppSource } = useLayout();
const { setAuth } = useAuthStore();
const { startSmartico } = useSmarticoInit();
const { currentRouteName } = useRoutes();
const { registerUserEvent } = useUserJourney();

const {
  isCaptchaRegisterEnabled,
  isCaptchaValidateCpfEnabled,
  isInvisibleCaptcha,
} = useCaptcha();

const {
  isRegisterRequestDocument,
  isRegisterRequestBirthDate,
  isRegisterRequestPhone,
  basicRegisterTitle,
} = useAuth();

const {
  isSocialAuthFlow,
  socialEmail,
  socialToken,
  socialService,
  clearSocialData,
} = useAuthSocial();

const {
  referenceCode,
  getParametersByCountry,
  utmSrc,
  utmSource,
  utmCampaign,
  utmContent,
  utmMedium,
  gaClientId,
  visitorFindedCountry,
  visitorHasAvailableCountry,
  visitorOrUserCountry,
  visitorOrUserLanguage,
  defaultCountry,
} = useParameters();

const isLudopathyTermAccepted = ref<boolean>(false);

const selectCountryRef = ref<InstanceType<typeof SelectCountry> | null>(null);

const listOfAvailableCountries = computed(() => {
  return selectCountryRef.value?.availableCountries ?? [];
});

const isSelectCountryFieldVisible = computed(() => {
  return (
    !visitorHasAvailableCountry.value &&
    listOfAvailableCountries.value.length > 1
  );
});

const finalSelectedCountry = computed<string>(() => {
  if (visitorHasAvailableCountry.value) {
    return visitorOrUserCountry.value;
  }

  if (!isSelectCountryFieldVisible.value) {
    return defaultCountry.value;
  }

  return selectCountryRef.value?.selectedCountry ?? '';
});

const dynamicParams = computed(() => {
  return getParametersByCountry(finalSelectedCountry.value);
});

const formValueEmail = ref<string>('');
const formValuePassword = ref<string>('');
const formValueAffiliationCode = ref<string>(referenceCode.value);
const isLoadingRegister = ref<boolean>(false);

const passwordFieldError = ref<string>('');

const onBlurPasswordField = () => {
  if (formValuePassword.value.length < 6) {
    passwordFieldError.value = t('auth.register.error.password_length');
  }
};

const emailFieldError = ref<string>('');

const onBlurEmailField = () => {
  if (!isEmailValid(formValueEmail.value)) {
    emailFieldError.value = t('auth.register.error.email_invalid');
  }
};

const antiRobotRef = ref<InstanceType<typeof AntiRobot> | null>(null);

const antiRobotToken = computed<string>(
  () => antiRobotRef.value?.finalCaptchaTokenValue ?? '',
);

const captchaComponentClass = computed<string>(() => {
  if (!isCaptchaRegisterEnabled.value && !isCaptchaValidateCpfEnabled.value) {
    return 'absolute opacity-0 -z-10 pointer-events-none mt-0 mb-0';
  }

  if (isInvisibleCaptcha.value) {
    return 'mt-2 mb-0 relative';
  }

  return 'mt-5 -mb-3';
});

function resetAntiRobotToken() {
  antiRobotRef.value?.$resetAllCaptcha();
}

const inputPhoneRef = ref<InstanceType<typeof inputFullPhone> | null>(null);

const selectedPhoneNumber = computed<string>(() => {
  return inputPhoneRef.value?.selectedPhoneNumber ?? '';
});

const selectedPhoneDdi = computed<string>(() => {
  return inputPhoneRef.value?.selectedPhoneDdi ?? '';
});

const documentsRef = ref<InstanceType<typeof Documents> | null>(null);

const finalDocumentNumber = computed<string>(() => {
  return documentsRef.value?.documentNumberClean ?? '';
});

const finalDocumentName = computed<string>(() => {
  return documentsRef.value?.documentName ?? '';
});

const finalDocumentBirthDate = computed<string>(() => {
  return documentsRef.value?.documentBirthDate ?? '';
});

const showRefCodeField = ref<boolean>(false);

const toggleRefCode = () => {
  showRefCodeField.value = !showRefCodeField.value;
};

const requestDocumentCPF = computed<boolean>(() => {
  return (
    finalSelectedCountry.value === 'BRA' && isRegisterRequestDocument.value
  );
});

const requestDocumentCURP = computed<boolean>(() => {
  return (
    finalSelectedCountry.value === 'MEX' && isRegisterRequestDocument.value
  );
});

const isDocumentFieldVisible = computed<boolean>(() => {
  return isRegisterRequestDocument.value || isRegisterRequestBirthDate.value;
});

const activeSubmitButton = computed<boolean>(() => {
  if (isCaptchaRegisterEnabled.value && !antiRobotToken.value) {
    return false;
  }

  if (isRegisterRequestPhone.value) {
    if (selectedPhoneNumber.value.length < 4) {
      return false;
    }

    if (selectedPhoneDdi.value.length < 1) {
      return false;
    }
  }

  if (requestDocumentCPF.value) {
    if (
      !finalDocumentNumber.value ||
      !finalDocumentName.value ||
      !finalDocumentBirthDate
    ) {
      return false;
    }
  }

  if (requestDocumentCURP.value) {
    if (!finalDocumentNumber.value || finalDocumentNumber.value.length < 6) {
      return false;
    }
  }

  if (!isEmailValid(formValueEmail.value)) {
    return false;
  }

  if (formValueEmail.value.length < 5) {
    return false;
  }

  if (!isSocialAuthFlow.value && formValuePassword.value.length < 6) {
    return false;
  }

  if (isRegisterRequestBirthDate.value) {
    if (!finalDocumentBirthDate.value) {
      return false;
    }
  }

  return true;
});

const { fieldError, fieldErrorEffect, setFieldError } = useFieldError();

const submitFormRegister = () => {
  if (isRegisterRequestPhone.value) {
    if (!selectedPhoneNumber.value) {
      openToaster({
        message: 'Digite o seu telefone',
        type: 'error',
      });

      return;
    }
    if (!selectedPhoneDdi.value) {
      openToaster({
        message: t('auth.register.error.ddi'),
        type: 'error',
      });

      return;
    }
  }

  if (!isLudopathyTermAccepted.value && isLudopathyValidationEnabled.value) {
    setFieldError(
      'ludopathy',
      t('auth.register.ludopathy.errors.accept_terms'),
    );

    return;
  }

  if (isCaptchaRegisterEnabled.value && !antiRobotToken.value) {
    openToaster({
      message: t('auth.login.verify_captcha'),
      type: 'warning',
    });

    return;
  }

  const submitData: IRegisterBasicSubmitData = {
    email: formValueEmail.value,
    password: formValuePassword.value,
    affiliation_code: String(formValueAffiliationCode.value),
    country: finalSelectedCountry.value,
    language: visitorOrUserLanguage.value,
    app_source: isAppSource.value ? 'app' : 'web',
    captcha_token: antiRobotToken.value,
    terms: true,
  };

  if (isLudopathyValidationEnabled.value) {
    submitData.ludopathy_term_accepted = isLudopathyTermAccepted.value;
  }

  if (isRegisterRequestPhone.value) {
    submitData.phone = selectedPhoneNumber.value;
    submitData.ddi = selectedPhoneDdi.value;
  }

  if (requestDocumentCPF.value) {
    submitData.number = finalDocumentNumber.value;
    submitData.name = finalDocumentName.value;
    submitData.birth_date = finalDocumentBirthDate.value;
  }

  if (requestDocumentCURP.value) {
    submitData.number = finalDocumentNumber.value;
  }

  if (requestDocumentCURP.value) {
    submitData.number = finalDocumentNumber.value;
  }

  if (isRegisterRequestBirthDate.value) {
    submitData.birth_date = finalDocumentBirthDate.value;
  } else if (finalSelectedCountry.value === 'MEX') {
    submitData.birth_date = '1990-01-01';
  }

  if (utmSrc.value) {
    submitData.src = utmSrc.value;
  }

  if (utmSource.value) {
    submitData.utm_source = utmSource.value;
  }

  if (utmCampaign.value) {
    submitData.utm_campaign = utmCampaign.value;
  }

  if (utmContent.value) {
    submitData.utm_content = utmContent.value;
  }

  if (utmMedium.value) {
    submitData.utm_medium = utmMedium.value;
  }

  if (gaClientId.value) {
    submitData.ga_client_id = gaClientId.value;
  }

  if (isSocialAuthFlow.value) {
    submitData.email = socialEmail.value;
    submitData.token = socialToken.value;
    submitData.service = socialService.value;
  }

  isLoadingRegister.value = true;

  setTimeout(() => {
    submitApiRegister(submitData);
  }, 200);
};

const endpointRegister = computed(() => {
  if (
    !requestDocumentCPF.value &&
    !requestDocumentCURP.value &&
    !isRegisterRequestPhone.value
  ) {
    return '/auth/register/simplified';
  }

  return '/auth/register';
});

async function submitApiRegister(submitData: IRegisterBasicSubmitData) {
  const { ApiService } = useApiService();

  await ApiService.post(endpointRegister.value, submitData)
    .then(async (res) => {
      if (res.status.toString().startsWith('20')) {
        closeModal();

        openToaster({
          message: t('auth.register.success'),
          type: 'success',
        });

        clearSocialData();

        $metrics.push('gtm')?.handle({
          event: 'sign_up',
          ...gtmUserParams(res.data?.user),
        });

        $metrics.push('facebook')?.handle('track', 'Lead', {
          content_name: 'Cadastro',
          status: true,
          value: 1,
        });

        if (isKwaiPixelEnabled.value) {
          $metrics.push('kwai')?.handle('addToCart');
        }

        if (isTaboolaEnabled.value) {
          $metrics.push('taboola')?.handle('lead');
        }

        if (res.data) {
          if (window.self !== window.top) {
            window.parent.postMessage({
              message: 'loginSuccess',
              responseData: res.data,
              responseHeaders: {
                'X-INFOS-ID':
                  res.headers.get('X-INFOS-ID') ||
                  res.headers.get('X-Infos-Id') ||
                  res.headers.get('x-infos-id'),
                'X-INFOS-REF':
                  res.headers.get('X-INFOS-REF') ||
                  res.headers.get('X-Infos-Ref') ||
                  res.headers.get('x-infos-ref'),
              },
            });
          }

          await setAuth(res);
          registerUserEvent();

          if (currentRouteName.value === 'register') {
            await navigateTo(localeLink('/'));
          } else {
            push({
              query: {
                ...currentRoute.value.query,
              },
            });
          }

          startSmartico();
        }
      } else {
        openToaster({
          message: t('auth.register.error.status_error'),
          type: 'error',
        });
      }
    })
    .catch((err) => {
      isLoadingRegister.value = false;

      const errorData = err.data;

      if (errorData) {
        const errorMessage = errorData.message
          ? errorData.message
          : t('auth.register.error.response_error');

        openToaster({
          message: errorMessage,
          type: 'error',
        });
      } else {
        const errorMessage = t('auth.register.error.api_error');

        openToaster({
          message: errorMessage,
          type: 'error',
        });
      }
    })
    .finally(() => {
      if (currentRouteName.value !== 'register') {
        isLoadingRegister.value = false;
      }

      if (localStorage.getItem('register_data')) {
        localStorage.removeItem('register_data');
      }
    });
}

onMounted(() => {
  console.debug('🏴 ? Country: ', visitorFindedCountry.value);
  console.debug('🏳️ ! Country: ', visitorOrUserCountry.value);

  setTimeout(() => {
    const localStorageData = localStorage.getItem('register_data');

    if (localStorageData && localStorageData !== null) {
      const params = JSON.parse(localStorageData);

      formValueEmail.value = params.email || '';
      formValuePassword.value = params.password || '';
    }
  }, 10);

  if (referenceCode.value) {
    showRefCodeField.value = true;
  }

  if (isKwaiPixelEnabled.value) {
    $metrics
      .push('kwai')
      ?.handle('contentView', { content_name: 'Página de Cadastro' });
  }

  if (isSocialAuthFlow.value) {
    formValueEmail.value = socialEmail.value;
  }
});
</script>

<template>
  <ClientOnly>
    <div
      class="w-full p-5"
      :class="$style.container"
    >
      <div
        v-if="!!basicRegisterTitle"
        class="text-center text-auth-titles mb-5 -mt-2 text-sm px-2"
      >
        {{ basicRegisterTitle }}
      </div>

      <form
        class="w-full max-w-[350px] md:max-w-[360px] flex flex-col gap-2 mx-auto"
        @submit.prevent="submitFormRegister"
      >
        <BaseLoading
          class="!bg-auth-bg-primary"
          :icon-loader="true"
          :show="isLoadingRegister"
        />

        <div :class="[$style.emailField]">
          <BaseFormInput
            id="email"
            v-model="formValueEmail"
            autocomplete="off"
            :disabled="isSocialAuthFlow"
            :error="emailFieldError"
            :initial-value="formValueEmail"
            :label="$t('auth.register.email')"
            :name="'email'"
            :required="true"
            type="email"
            @blur="onBlurEmailField"
            @focus="emailFieldError = ''"
          />

          <div
            v-if="isSocialAuthFlow"
            :class="$style.socialAuthInfo"
          >
            <NuxtIcon
              aria-hidden="true"
              filled
              :name="`social/${socialService}-color`"
            />
          </div>
        </div>

        <BaseFormInput
          v-if="!isSocialAuthFlow"
          id="password"
          v-model="formValuePassword"
          autocomplete="off"
          :error="passwordFieldError"
          :label="$t('auth.register.password')"
          :name="'password'"
          :required="true"
          :show-hide-pass="true"
          @blur="onBlurPasswordField"
          @focus="passwordFieldError = ''"
        />

        <BaseFormSelectCountry
          v-if="isSelectCountryFieldVisible"
          ref="selectCountryRef"
          :user-can-change-country="true"
        />

        <BaseFormDocuments
          v-if="isDocumentFieldVisible"
          ref="documentsRef"
          :anti-robot-token="antiRobotToken"
          :dynamic-params="dynamicParams"
          :is-register-screen="true"
          :selected-country="finalSelectedCountry"
          @reset-anti-robot-token="resetAntiRobotToken"
        />

        <BaseFormInputFullPhone
          v-if="isRegisterRequestPhone"
          id="phone"
          ref="inputPhoneRef"
          :required="true"
          :selected-country="finalSelectedCountry"
        />

        <BaseFormInput
          v-if="showRefCodeField"
          id="affiliation_code"
          v-model="formValueAffiliationCode"
          autocomplete="off"
          :initial-value="formValueAffiliationCode"
          :label="$t('auth.register.reference_code')"
          name="affiliation_code"
          :readonly="!!referenceCode"
          :required="false"
          type="text"
        />

        <section
          v-if="isLudopathyValidationEnabled"
          :class="[
            $style.checkboxSection,
            { [$style.hasError]: fieldError === 'ludopathy' },
            { [$style.fieldHasError]: fieldErrorEffect === 'ludopathy' },
          ]"
        >
          <BaseFormCheckbox
            v-model="isLudopathyTermAccepted"
            :label="$t('auth.register.ludopathy.optin')"
            value="ludopathy"
          />
        </section>

        <button
          v-else
          class="text-auth-texts text-xs font-medium mt-2"
          type="button"
          @click="toggleRefCode"
        >
          {{ $t('auth.register.reference_code') }}
        </button>

        <div
          class="text-[.6rem] md:text-[.62rem] text-auth-texts/60 text-center my-1"
        >
          {{ $t('auth.register.by_signing_up') }}
          <a
            class="text-auth-links/80"
            :href="localeLink('/page/terms')"
            target="_blank"
          >
            {{ $t('auth.register.agree_terms_conditions') }}
          </a>
        </div>

        <div :class="$style.buttonBox">
          <button
            :class="$style.btnSubmit"
            :disabled="!activeSubmitButton"
            type="submit"
          >
            {{ $t('auth.register.register') }}
            <NuxtIcon name="arrows/arrow_right" />
          </button>

          <div :class="captchaComponentClass">
            <BaseFormAntiRobot ref="antiRobotRef" />
          </div>
        </div>

        <div
          v-if="isSocialAuthFlow"
          class="mt-4"
        />

        <AuthSocialAuth
          :buttons-only="isSocialAuthFlow"
          with-titles
        />

        <BaseModalFooter />
      </form>
    </div>
  </ClientOnly>
</template>

<style module lang="scss">
.emailField {
  @apply relative;

  .socialAuthInfo {
    @apply absolute right-0 top-0 text-lg flex justify-center items-center w-[46px] sm:w-[50px] h-[46px] sm:h-[50px];
  }
}

.container {
  .checkboxSection {
    @apply px-3 py-2 bg-auth-bg-inputs rounded-lg flex items-start;

    &.hasError {
      box-shadow: 0 0 200px inset rgba(255, 0, 0, 0.1);
    }

    &.fieldHasError {
      @apply border-error;
      animation: shake 200ms ease-in-out;
    }

    p {
      @apply text-2xs leading-4;
    }

    a {
      @apply text-primary underline cursor-pointer whitespace-nowrap;
    }

    b {
      @apply font-semibold text-primary;
    }
  }
}

.btnSubmit {
  @apply rounded-md flex items-center justify-center gap-2 bg-auth-button-bg text-auth-button-text text-base font-semibold w-full px-3 py-3;

  &[disabled]:not([disabled='false']) {
    @apply opacity-40 cursor-not-allowed;
  }

  svg {
    @apply text-sm;
  }
}

.buttonBox {
  @apply relative w-full;
}
</style>
